import React from "react";
import { Box, Typography, Container } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useAFTheme } from "@theme/.";
import WavyImg from "@assets/WavyImg.png";
import {
  AVIATION_COMPLIANCEDETAIL,
  AVIATION_COST_TITLE1,
  AVIATION_COST_TITLE2,
  AVIATION_COST_TITLE3,
  AVIATION_TRAININGDETAIL,
  COMPLIANCEDETAIL,
  COST_TITLE1,
  COST_TITLE2,
  COST_TITLE3,
  TRAININGDETAIL,
  USECASE_TITLE,
} from "@constants/unicomply-keys";
import type { LandingVertical } from "@components/UniComply_Features/UniComply_Features";
import CostIcon from "@icons/CostIcon";
import ComplianceIcon from "@icons/UseCaseCompIcon";
import TrainingIcon from "@icons/TrainingIcon";

const UseCaseSection = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  minHeight: 540,
  backgroundImage: `url(${WavyImg})`,
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  paddingTop: 40,
  paddingBottom: 40,
  [theme.breakpoints.up("md")]: {
    minHeight: 738,
    paddingTop: 0,
    paddingBottom: 0,
  },
}));

const PainPointContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 25,
  minHeight: 90,
  width: 499,
  marginBottom: 24,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    gap: 16,
    minHeight: "auto",
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: 65,
  height: 56,
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    width: 56,
    height: 48,
  },
}));

const IconBackground = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 7,
  left: 15.5,
  width: 59,
  height: 58,
  background:
    "var(--Gradient-4, linear-gradient(211deg, var(--Primary-Color-Palette-100, #E5F5F8) 44.85%, var(--Primary-Color-Palette-200, #BFE5EC) 80.27%))",
  borderRadius: "18px 5px 5px 10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.down("sm")]: {
    top: 6,
    left: 12,
    width: 52,
    height: 50,
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: -18,
  left: -15.5,
  width: 76,
  height: 76,
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.down("sm")]: {
    top: -14,
    left: -12,
    width: 68,
    height: 68,
  },
}));

const PainPointText = styled(Box)(({ theme }) => ({
  flex: 1,
  width: 370,
  minHeight: 55,
  display: "flex",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    width: "auto",
  },
}));

interface PainPointProps {
  icon: React.ReactNode;
  children: React.ReactNode;
}

function PainPoint({ icon, children }: PainPointProps) {
  return (
    <PainPointContainer>
      <IconContainer>
        <IconBackground />
        <IconWrapper>{icon}</IconWrapper>
      </IconContainer>
      <PainPointText>
        <Typography
          component="div"
          sx={{
            fontWeight: 400,
            fontSize: 19,
            lineHeight: "28px",
            color: "rgba(255, 255, 255, 0.9)",
          }}
        >
          {children}
        </Typography>
      </PainPointText>
    </PainPointContainer>
  );
}

export default function UniComply_UseCases({
  vertical = "healthcare",
}: {
  vertical?: LandingVertical;
}) {
  useAFTheme();
  const isAviation = vertical === "aviation";

  return (
    <UseCaseSection id="use-cases">
      {/* <BackgroundPattern /> */}

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 10 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            maxWidth: 1200,
            margin: "0 auto",
            gap: { xs: 3, md: 0 },
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* Main Title */}
          <Box sx={{ flex: "0 0 auto" }}>
            <Typography
              sx={{
                color: "var(--Brand-Colors-color--grad-1, #35D7E4)",
                fontFamily: '"Rethink Sans", sans-serif',
                fontSize: { xs: "40px", sm: "50px", md: "64px", lg: "70px" },
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: { xs: "44px", sm: "52px", md: "64px", lg: "70px" },
              }}
            >
              {USECASE_TITLE}
            </Typography>
          </Box>

          {/* Pain Points */}
          <Box
            sx={{
              width: { xs: "100%", md: 494 },
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <PainPoint icon={<TrainingIcon />}>
              <Typography
                sx={{
                  color: "var(--Brand-Colors-Brand-Light-Bacground, #E6FAFC)",
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: { xs: "18px", sm: "20px", md: "22px", lg: "24px" },
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: { xs: "26px", sm: "28px", md: "28px", lg: "30px" },
                  ml: "13px",
                }}
              >
                {isAviation ? AVIATION_TRAININGDETAIL : TRAININGDETAIL}
              </Typography>
            </PainPoint>

            <PainPoint icon={<ComplianceIcon />}>
              <Typography
                sx={{
                  color: "var(--Brand-Colors-Brand-Light-Bacground, #E6FAFC)",
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: { xs: "18px", sm: "20px", md: "22px", lg: "24px" },
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: { xs: "26px", sm: "28px", md: "28px" },
                  ml: "13px",
                }}
              >
                {isAviation ? AVIATION_COMPLIANCEDETAIL : COMPLIANCEDETAIL}
              </Typography>
            </PainPoint>
            <PainPoint icon={<CostIcon />}>
              <Typography
                sx={{
                  color: "var(--Brand-Colors-Brand-Light-Bacground, #E6FAFC)",
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: { xs: "18px", sm: "20px", md: "22px", lg: "24px" },
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: { xs: "26px", sm: "28px", md: "28px" },
                  ml: "13px",
                }}
              >
                <Box component="span">
                  {isAviation ? AVIATION_COST_TITLE1 : COST_TITLE1}
                </Box>
                <Box
                  component="span"
                  sx={{ fontWeight: 600, color: "#ffffff" }}
                >
                  {isAviation ? AVIATION_COST_TITLE2 : COST_TITLE2}
                </Box>
                <Box component="span">
                  {isAviation ? AVIATION_COST_TITLE3 : COST_TITLE3}
                </Box>
              </Typography>
            </PainPoint>
          </Box>
        </Box>
      </Container>
    </UseCaseSection>
  );
}
